import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, CellValueChangedEvent, GridApi, ColumnApi, GridReadyEvent } from 'ag-grid-community';
import Overlay from 'lib/Overlay';
import Button, { Color } from 'lib/Button';
import useEditDirectSalesLine from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useEditDirectSalesLine';
import { DirectSalesObjectiveTarget } from 'components/common/types/DirectSalesCampaign.types';
import moneySvg from 'assets/icons/money.svg';
import framesSvg from 'assets/icons/frame.svg';
import eyeSvg from 'assets/icons/eye_empty.svg';
import formatsSvg from 'assets/icons/formats.svg';
import locationSvg from 'assets/icons/location.svg';
import poisSvg from 'assets/icons/pois.svg';
import tagSvg from 'assets/icons/tag.svg';
import routeFrameListSvg from 'assets/icons/route_frame_lists.svg';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './ObjectivesModal.pcss';
import {
  FilterCellRenderer,
  FrameListCellRenderer,
  POICellRenderer,
  TagsCellRenderer,
  TargetCellRenderer,
} from './CellRenderers';
import { InputNumberCellEditor } from './CellEditors';
import { HeaderWithIcon, SeparatorHeader } from './CustomHeaders';
import { ObjectivesModalProps, EditableTargetField, RowDataItem } from './ObjectivesModal.types';
import { createRowData, isEditableTargetField, getAllocationField, isFrameList, hasFrameListData } from './utils';

export const ObjectivesModal: React.FC<ObjectivesModalProps> = ({
  objectives,
  lineAllocation = {},
  isOpen,
  onClose,
  onSave,
}: ObjectivesModalProps) => {
  const editLine = useEditDirectSalesLine();

  const [editedTargets, setEditedTargets] = useState<Set<string>>(new Set());
  const [rowData, setRowData] = useState<RowDataItem[]>([]);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);

  useEffect(() => {
    if (isOpen) {
      const initialRowData = createRowData(objectives, lineAllocation);
      setRowData(initialRowData);
    } else {
      setRowData([]);
    }

    setEditedTargets(new Set());
  }, [isOpen]);

  useEffect(() => {
    if (columnApiRef.current && rowData.length > 0) {
      requestAnimationFrame(() => {
        const columnApi = columnApiRef.current;
        if (!columnApi) return;

        const targetColumns = ['impressions', 'frames', 'budget'];
        columnApi.autoSizeColumns(targetColumns, false);
      });
    }
  }, [rowData, isOpen]);

  const createValueSetter = useCallback((field: EditableTargetField) => {
    return (params: { data: RowDataItem; newValue: number | undefined }) => {
      const { data, newValue } = params;

      if (!newValue) {
        return false;
      }

      data[field].value = newValue;
      return true;
    };
  }, []);

  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    const { data, newValue, colDef, node } = event;
    const field = colDef?.field;

    if (!data || !field || !isEditableTargetField(field)) return;
    if (!newValue) return;

    const allocationField = getAllocationField(field);
    const { objectiveGroupId } = data;
    const editedKey = `${objectiveGroupId}-${field}`;
    setEditedTargets((prev) => new Set(prev).add(editedKey));

    data[allocationField] = { allocated: undefined, status: undefined };

    if (gridApiRef.current && node) {
      gridApiRef.current.refreshCells({
        rowNodes: [node],
        columns: [field],
        force: true,
      });
    }
    setIsSaveEnabled(true);
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;
  }, []);

  const allColumnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: '',
      minWidth: 200,
      pinned: 'left',
      cellClass: 'sub-header-base',
    },
    {
      field: 'locations',
      headerName: 'Location',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: locationSvg,
        label: 'Location',
      },
      minWidth: 230,
      flex: 2,
      autoHeight: true,
      cellRenderer: FilterCellRenderer,
    },
    {
      field: 'formats',
      headerName: 'Format',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: formatsSvg,
        label: 'Format',
      },
      minWidth: 230,
      flex: 2,
      autoHeight: true,
      cellRenderer: FilterCellRenderer,
    },
    {
      field: 'poi',
      headerName: 'POI',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: poisSvg,
        label: 'POI',
      },
      minWidth: 230,
      flex: 2,
      autoHeight: true,
      cellRenderer: POICellRenderer,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: tagSvg,
        label: 'Tags',
      },
      minWidth: 230,
      flex: 2,
      autoHeight: true,
      cellRenderer: TagsCellRenderer,
    },
    {
      field: 'frameList',
      headerName: 'Frame List',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: routeFrameListSvg,
        label: 'Frame List',
      },
      minWidth: 230,
      flex: 2,
      autoHeight: true,
      cellRenderer: FrameListCellRenderer,
    },
    {
      field: 'separator',
      headerName: '',
      width: 4,
      minWidth: 4,
      resizable: false,
      sortable: false,
      cellStyle: {
        backgroundColor: 'var(--neutral-300)',
      },
      headerComponent: SeparatorHeader,
      suppressMenu: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: eyeSvg,
        label: 'Impressions',
      },
      minWidth: 160,
      editable: true,
      singleClickEdit: true,
      cellEditor: InputNumberCellEditor,
      valueSetter: createValueSetter('impressions'),
      cellRenderer: TargetCellRenderer,
      cellRendererParams: {
        field: 'impressions',
        allocationField: 'impressionsAllocation',
        editedTargets,
      },
    },
    {
      field: 'frames',
      headerName: 'Frames',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: framesSvg,
        label: 'Frames',
      },
      minWidth: 160,
      editable: true,
      singleClickEdit: true,
      cellEditor: InputNumberCellEditor,
      valueSetter: createValueSetter('frames'),
      cellRenderer: TargetCellRenderer,
      cellRendererParams: {
        field: 'frames',
        allocationField: 'framesAllocation',
        editedTargets,
      },
    },
    {
      field: 'budget',
      headerName: 'Budget',
      headerComponent: HeaderWithIcon,
      headerComponentParams: {
        icon: moneySvg,
        label: 'Budget',
      },
      minWidth: 160,
      editable: true,
      singleClickEdit: true,
      cellEditor: InputNumberCellEditor,
      valueSetter: createValueSetter('budget'),
      cellRenderer: TargetCellRenderer,
      cellRendererParams: {
        field: 'budget',
        allocationField: 'budgetAllocation',
        editedTargets,
        decimalPlaces: 2,
      },
    },
  ];

  const columnDefs: ColDef[] = useMemo(() => {
    const PERSISTENT_COLUMNS = ['objectiveGroupId', 'name'];
    const columnsWithData = new Set<string>(PERSISTENT_COLUMNS);

    const shouldIncludeColumn = (key: string, value: RowDataItem[keyof RowDataItem]): boolean => {
      if (PERSISTENT_COLUMNS.includes(key)) return false;

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (key === 'frameList' && isFrameList(value)) {
        return hasFrameListData(value);
      }

      return !!value;
    };

    rowData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const typedKey = key as keyof RowDataItem;
        const isTargetField = ['budget', 'frames', 'impressions'].includes(typedKey);
        const value = isTargetField ? (row[typedKey] as DirectSalesObjectiveTarget)?.value : row[typedKey];

        if (shouldIncludeColumn(key, value)) {
          columnsWithData.add(key);
        }
      });
    });

    return allColumnDefs.filter((colDef) => {
      const field = colDef.field || '';
      return field === 'separator' || columnsWithData.has(field);
    });
  }, [rowData]);

  const gridOptions: GridOptions = {
    suppressMovableColumns: true,
    suppressContextMenu: true,
    suppressBrowserResizeObserver: true,
    stopEditingWhenCellsLoseFocus: true,
    defaultColDef: {
      resizable: true,
      suppressMenu: true,
    },
    onCellValueChanged,
    onGridReady,
  };

  const handleSave = useCallback(async () => {
    try {
      setIsSaveEnabled(false);

      if (onSave) {
        await onSave(rowData);
      } else {
        await editLine({ directSalesData: { objectives: rowData } });
      }
      onClose();
    } catch {
      setIsSaveEnabled(true);
    }
  }, [rowData, onSave]);

  return (
    <Overlay
      isOpen={isOpen}
      onClose={onClose}
      theme="light"
      overflowYScroll
      header={<h2 className="text-xl font-semibold">Objectives Summary</h2>}
      footer={
        <div className="w-full flex justify-end gap-2 h-1/10">
          <Button label="Cancel" color={Color.BLACK_AND_WHITE} onClick={onClose} />
          <Button label="Save" color={Color.PRIMARY} onClick={handleSave} isDisabled={!isSaveEnabled} />
        </div>
      }
    >
      <div data-test-id="objectives-summary-modal" className="w-full h-full">
        <div className="ag-theme-material objectives-table h-full">
          <AgGridReact rowData={rowData} columnDefs={columnDefs} gridOptions={gridOptions} />
        </div>
      </div>
    </Overlay>
  );
};
